import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent, Grid, Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';

import { getHierarchyUserLevelList } from '../../store/Slice/Onboarding/OnboardingSlice';
import HierarchyUserLevelListData from '../../store/Slice/Onboarding/OnboardingSelector';

const MAX_CHILD_PER_LEVEL = 10;

/* =======================
   Tree Node Component
======================= */
function TreeNode({ node, level = 0 }) {
  const [isOpen, setIsOpen] = useState(false); // toggle state
  if (!node) return null;

  const isEmployee = node.userTypeLabel === 'EMPLOYEE';

  const userTypeColor = isEmployee
    ? '#1976d2' // MUI primary blue
    : '#6d4c41';

  const isSelected = Boolean(node.currentUser);
  const children = Array.isArray(node.children) ? node.children.slice(0, MAX_CHILD_PER_LEVEL) : [];

  const hasChildren = children.length > 0;

  const handleToggle = () => {
    if (hasChildren) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <Box sx={{ ml: level * 3, mt: 1 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        whileHover={{ scale: 1.02 }}
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: hasChildren ? 'pointer' : 'default'
        }}
      >
        {/* Partner Card */}
        <Box
          sx={{
            border: `1px solid ${userTypeColor}`,
            p: '10px 16px',
            borderRadius: '14px',
            width: 320,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: isSelected ? userTypeColor : `${userTypeColor}15`, // light tint
            color: isSelected ? '#fff' : userTypeColor,
            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
            fontWeight: 600,
            userSelect: 'none'
          }}
        >
          {/* Arrow rotate on open */}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
            {hasChildren ? (
              <motion.div
                animate={{ rotate: isOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <ChevronRight size={18} />
              </motion.div>
            ) : (
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: '#e0e0e0'
                }}
              />
            )}
          </motion.div>

          {node.partnerName}
        </Box>

        {/* Badge */}
        {isSelected && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              padding: '4px 10px',
              background: 'rgba(255,193,7,0.15)',
              color: '#B78103',
              border: '1px solid rgb(223,205,52)',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap'
            }}
          >
            CURRENT USER
          </motion.div>
        )}

        {/* Count */}
        {/* Count */}
        <Typography variant="caption" sx={{ ml: 1, color: '#777' }}>
          Direct: {node.directCount} | Total: {node.totalCount}
        </Typography>

        {/* Has Children Indicator */}
        <Typography
          variant="caption"
          sx={{
            ml: 1,
            color: hasChildren ? '#2e7d32' : '#9e9e9e',
            fontWeight: 600
          }}
        >
          {hasChildren ? 'Has Team' : 'No Team'}
        </Typography>
      </motion.div>

      {/* Children (toggle controlled) */}
      {isOpen && hasChildren && (
        <Box
          sx={{
            borderLeft: '2px dashed #cfcfcf',
            ml: 3,
            pl: 2,
            mt: 1
          }}
        >
          {children.map((child) => (
            <TreeNode key={child.partnerId} node={child} level={level + 1} />
          ))}
        </Box>
      )}

      {/* Limit Warning */}
      {isOpen && node.children?.length > MAX_CHILD_PER_LEVEL && (
        <Typography variant="caption" sx={{ ml: 6, color: '#d32f2f', fontWeight: 600 }}>
          Showing only first {MAX_CHILD_PER_LEVEL} partners
        </Typography>
      )}
    </Box>
  );
}

/* =======================
   Main Page
======================= */
export default function PartnerHierarchyPage({ partnerId }) {
  const { HierarchyUserLevelListData: hierarchyData } = HierarchyUserLevelListData();
  // console.log('HierarchyUserLevelListData222', hierarchyData);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getHierarchyUserLevelList({ partnerId }));
  }, [partnerId, dispatch]);

  const rootNode = Array.isArray(hierarchyData) ? hierarchyData[0] : null;

  return (
    <Grid item xs={12}>
      <Typography
        style={{ marginTop: '30px' }}
        variant="body1"
        className="headingLineUp headingLineUpOut"
      >
        Partner Hierarchy Structure
      </Typography>

      <Box className="viewLeadCard cardTopMinusMargin">
        <Card sx={{ p: 2 }}>
          <CardContent>
            {rootNode ? (
              <TreeNode node={rootNode} />
            ) : (
              <Typography color="text.secondary">No hierarchy data available</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Grid>
  );
}
